import React, { useMemo, useRef } from 'react';
import {
  RiArrowLeftSLine, RiArrowRightSLine, RiTimeLine, RiTeamLine,
  RiCalendarLine, RiSparkling2Line, RiCheckLine, RiArrowDownSLine,
} from 'react-icons/ri';

// Brand palette — derived from the Nexcess logo gradient.
const BRAND = {
  blue: '#0984e3',
  cyan: '#00b0ff',
  purple: '#7c3aed',
  magenta: '#d500f9',
  gradient: 'linear-gradient(135deg,#0984e3 0%,#00b0ff 55%,#d500f9 100%)',
  gradientSoft: 'linear-gradient(135deg,rgba(9,132,227,0.10),rgba(213,0,249,0.10))',
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function toLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtHM(ms) {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

function fmtCompact(ms) {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h >= 1) return `${h}h${m ? ` ${m}m` : ''}`;
  return `${m}m`;
}

// Activity tier (0-3) → drives the soft pastel cell tint.
function intensityTier(ms) {
  const h = ms / 3_600_000;
  if (h < 0.5) return 0;
  if (h < 3) return 1;
  if (h < 6) return 2;
  return 3;
}

// Soft, sweet pastel fills using the brand palette.
const TIER_LOOK = [
  // 0 — none
  { bg: '#fff', dot: '#E2E8F0', text: '#0F172A' },
  // 1 — light blue pastel
  { bg: 'linear-gradient(135deg,#EBF8FF 0%,#F5FBFF 100%)', dot: BRAND.cyan, text: '#0369A1' },
  // 2 — brand blue tint
  { bg: 'linear-gradient(135deg,#DCEEFC 0%,#F0E7FE 100%)', dot: BRAND.blue, text: '#1D4ED8' },
  // 3 — brand finale (blue → purple → magenta) tint
  { bg: 'linear-gradient(135deg,#C7DDFE 0%,#E4D2FB 60%,#FAD0F4 100%)', dot: BRAND.magenta, text: '#7E22CE' },
];

export default function AttendanceCalendar({
  month,
  year,
  onMonthChange,
  entries = [],
  onDayClick,
  showUserSummary = false,
  loading = false,
}) {
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => toLocalDate(today), [today]);

  // Hidden <input type="month"> we open programmatically when the user clicks
  // the month title — gives a native calendar dropdown for picking month+year.
  const monthPickerRef = useRef(null);
  const openMonthPicker = () => {
    const el = monthPickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') el.showPicker();
    else el.focus();
  };

  // Build 6×7 grid — Monday-first
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startWeekday = (first.getDay() + 6) % 7;
    const lastDay = new Date(year, month, 0).getDate();
    const out = [];
    const prevLastDay = new Date(year, month - 1, 0).getDate();
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = new Date(year, month - 2, prevLastDay - i);
      out.push({ date: d, dateStr: toLocalDate(d), inMonth: false });
    }
    for (let d = 1; d <= lastDay; d++) {
      const dt = new Date(year, month - 1, d);
      out.push({ date: dt, dateStr: toLocalDate(dt), inMonth: true });
    }
    let next = 1;
    while (out.length < 42) {
      const dt = new Date(year, month, next++);
      out.push({ date: dt, dateStr: toLocalDate(dt), inMonth: false });
    }
    return out;
  }, [month, year]);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const it of entries) {
      const raw = it.date || it.start_time;
      if (!raw) continue;
      const k = String(raw).slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return map;
  }, [entries]);

  const monthSummary = useMemo(() => {
    let totalMs = 0;
    let activeDays = 0;
    let workingNow = 0;
    const userIds = new Set();
    for (const [, items] of byDate) {
      let dayMs = 0;
      for (const it of items) {
        if (!it.start_time) continue;
        const start = new Date(it.start_time).getTime();
        const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
        dayMs += Math.max(0, end - start);
        if (!it.end_time) workingNow++;
        const uid = it.user_id || it.user?.id || it.user?._id;
        if (uid) userIds.add(uid);
      }
      totalMs += dayMs;
      if (dayMs > 0) activeDays++;
    }
    return { totalMs, activeDays, workingNow, users: userIds.size };
  }, [byDate]);

  const goPrev = () => {
    const m = month === 1 ? 12 : month - 1;
    const y = month === 1 ? year - 1 : year;
    onMonthChange?.({ month: m, year: y });
  };
  const goNext = () => {
    const m = month === 12 ? 1 : month + 1;
    const y = month === 12 ? year + 1 : year;
    onMonthChange?.({ month: m, year: y });
  };
  const goToday = () => {
    onMonthChange?.({ month: today.getMonth() + 1, year: today.getFullYear() });
  };
  const isCurrentMonth = today.getMonth() + 1 === month && today.getFullYear() === year;
  const avgPerDay = monthSummary.activeDays > 0 ? monthSummary.totalMs / monthSummary.activeDays : 0;

  return (
    <div className="relative bg-white rounded-[28px] overflow-hidden border border-[#EEF2F6]
                    shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_60px_-12px_rgba(9,132,227,0.18)]">

      {/* Decorative top sheen (matches logo gradient) */}
      <div className="absolute top-0 inset-x-0 h-[3px]" style={{ background: BRAND.gradient }} />
      {/* Soft corner blobs for sweetness */}
      <div
        className="absolute -top-24 -right-20 w-64 h-64 rounded-full opacity-[0.18] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle,${BRAND.magenta} 0%,transparent 70%)` }}
      />
      <div
        className="absolute -bottom-24 -left-20 w-64 h-64 rounded-full opacity-[0.15] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle,${BRAND.blue} 0%,transparent 70%)` }}
      />

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="relative px-7 pt-8 pb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3.5">
            {/* Brand-gradient icon */}
            <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-[0_10px_24px_-6px_rgba(9,132,227,0.55)]"
                 style={{ background: BRAND.gradient }}>
              <RiCalendarLine size={22} />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white shadow border border-[#EEF2F6] flex items-center justify-center">
                <RiSparkling2Line size={7} style={{ color: BRAND.magenta }} />
              </span>
            </div>
            <div className="relative">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                Attendance
              </p>
              {/* Clickable title — opens the native month picker */}
              <button
                type="button"
                onClick={openMonthPicker}
                className="group inline-flex items-center gap-1.5 mt-1 -mx-1 px-1 rounded-lg hover:bg-[#F8FAFC] active:bg-[#F1F5F9] transition-colors cursor-pointer"
                title="Pick month and year"
              >
                <h3 className="font-bold text-[26px] text-[#0F172A] tracking-tight leading-none">
                  {MONTH_NAMES[month - 1]}{' '}
                  <span className="font-medium bg-clip-text text-transparent"
                        style={{ backgroundImage: BRAND.gradient }}>
                    {year}
                  </span>
                </h3>
                <RiArrowDownSLine
                  size={20}
                  className="text-[#94A3B8] group-hover:text-[#0984e3] transition-colors mt-1"
                />
              </button>
              {/* Hidden native month picker — programmatically opened by the title click */}
              <input
                ref={monthPickerRef}
                type="month"
                value={`${year}-${String(month).padStart(2, '0')}`}
                onChange={(e) => {
                  const [y, m] = (e.target.value || '').split('-');
                  if (y && m) onMonthChange?.({ month: Number(m), year: Number(y) });
                }}
                aria-label="Pick month and year"
                className="sr-only absolute inset-0 opacity-0 pointer-events-none"
                tabIndex={-1}
              />
            </div>
          </div>

          {/* Nav */}
          <div className="flex items-center gap-1.5">
            <RoundBtn onClick={goPrev} ariaLabel="Previous month">
              <RiArrowLeftSLine size={18} />
            </RoundBtn>
            <button
              onClick={goToday}
              disabled={isCurrentMonth}
              className="h-10 px-4 rounded-full text-xs font-bold text-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_6px_16px_-4px_rgba(9,132,227,0.45)] hover:shadow-[0_8px_20px_-4px_rgba(213,0,249,0.45)] active:scale-95"
              style={{ background: BRAND.gradient }}
            >
              Current month
            </button>
            <RoundBtn onClick={goNext} ariaLabel="Next month">
              <RiArrowRightSLine size={18} />
            </RoundBtn>
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            icon={RiTimeLine}
            label="Total"
            value={loading ? '…' : fmtHM(monthSummary.totalMs)}
            tone={BRAND.blue}
            tint="#EBF6FE"
          />
          <StatCard
            icon={RiCheckLine}
            label="Active days"
            value={loading ? '…' : `${monthSummary.activeDays}`}
            sub={!loading && monthSummary.activeDays > 0 ? `${fmtCompact(avgPerDay)} avg` : null}
            tone={BRAND.cyan}
            tint="#E5F6FE"
          />
          <StatCard
            icon={showUserSummary ? RiTeamLine : RiCalendarLine}
            label={showUserSummary ? 'Employees' : 'Sessions'}
            value={loading ? '…' : showUserSummary ? `${monthSummary.users}` : `${entries.length}`}
            tone={BRAND.purple}
            tint="#F1EBFE"
          />
          <StatCard
            icon={RiSparkling2Line}
            label="Live now"
            value={loading ? '…' : monthSummary.workingNow > 0 ? `${monthSummary.workingNow}` : '0'}
            sub={!loading && monthSummary.workingNow > 0 ? 'Currently tracking' : 'Idle'}
            tone={BRAND.magenta}
            tint="#FCEBFB"
            pulse={monthSummary.workingNow > 0}
          />
        </div>
      </div>

      {/* ── Weekday header ──────────────────────────────────────────────────── */}
      <div className="relative px-4 mt-1 mb-2 grid grid-cols-7">
        {WEEKDAYS.map((d, i) => (
          <div
            key={d}
            className={`px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-center ${
              i === 6 ? 'text-[#d500f9]/70' : 'text-[#64748B]'
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* ── Day grid ───────────────────────────────────────────────────────── */}
      <div className="relative grid grid-cols-7 gap-1.5 px-4 pb-4">
        {cells.map((cell, idx) => {
          const items = byDate.get(cell.dateStr) || [];
          const hasEntries = items.length > 0;
          const isToday = cell.dateStr === todayStr;
          const isFuture = cell.date > today;
          const weekday = cell.date.getDay();
          // Indian work-week: all Sundays are holidays; odd-numbered Saturdays
          // (1st, 3rd, 5th of the month) are holidays, 2nd and 4th are working days.
          const saturdayOrdinal = weekday === 6 ? Math.ceil(cell.date.getDate() / 7) : 0;
          const isHoliday = weekday === 0 || (weekday === 6 && saturdayOrdinal % 2 === 1);

          let totalMs = 0;
          const userIds = new Set();
          let working = 0;
          for (const it of items) {
            if (!it.start_time) continue;
            const start = new Date(it.start_time).getTime();
            const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
            totalMs += Math.max(0, end - start);
            const uid = it.user_id || it.user?.id || it.user?._id;
            if (uid) userIds.add(uid);
            if (!it.end_time) working++;
          }

          const tier = intensityTier(totalMs);
          const look = TIER_LOOK[tier];
          const clickable = cell.inMonth && hasEntries;

          // 24-hour mini-timeline
          const dayStart = new Date(cell.date);
          dayStart.setHours(0, 0, 0, 0);
          const dayMs = 24 * 60 * 60 * 1000;
          const bars = items.slice(0, 12).map((it) => {
            if (!it.start_time) return null;
            const s = new Date(it.start_time).getTime();
            const e = it.end_time ? new Date(it.end_time).getTime() : Date.now();
            const startPct = Math.max(0, Math.min(100, ((s - dayStart.getTime()) / dayMs) * 100));
            const endPct = Math.max(0, Math.min(100, ((e - dayStart.getTime()) / dayMs) * 100));
            return { left: startPct, w: Math.max(1.5, endPct - startPct), open: !it.end_time };
          }).filter(Boolean);

          return (
            <button
              key={idx}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onDayClick?.(cell.dateStr, items)}
              className={`group relative rounded-2xl text-left p-3 min-h-[118px] transition-all duration-300 overflow-hidden border ${
                clickable ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default'
              } ${!cell.inMonth ? 'opacity-50' : ''}`}
              style={{
                background: !cell.inMonth ? '#FAFBFC' : isToday ? BRAND.gradientSoft : look.bg,
                borderColor: isToday
                  ? 'rgba(9,132,227,0.35)'
                  : hasEntries
                    ? 'rgba(15,23,42,0.04)'
                    : '#F1F5F9',
                boxShadow: hasEntries && cell.inMonth
                  ? '0 1px 2px rgba(15,23,42,0.02), 0 6px 16px -8px rgba(9,132,227,0.18)'
                  : 'none',
              }}
            >
              {/* Hover glow */}
              {clickable && (
                <span
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ boxShadow: '0 12px 28px -8px rgba(124,58,237,0.32)' }}
                />
              )}

              {/* Top row */}
              <div className="relative flex items-start justify-between mb-2">
                {isToday ? (
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-[13px] shadow-[0_6px_16px_-4px_rgba(9,132,227,0.55)]"
                    style={{ background: BRAND.gradient }}
                  >
                    {cell.date.getDate()}
                  </span>
                ) : (
                  <span
                    className={`text-[14px] font-bold tabular-nums ${
                      !cell.inMonth
                        ? 'text-[#CBD5E1]'
                        : isHoliday
                          ? 'text-[#d500f9]'
                          : 'text-[#0F172A]'
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>
                )}
                <div className="flex items-center gap-1">
                  {working > 0 && (
                    <span className="relative flex h-2 w-2" title={`${working} working`}>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                            style={{ background: BRAND.magenta }} />
                      <span className="relative inline-flex rounded-full h-2 w-2"
                            style={{ background: BRAND.magenta }} />
                    </span>
                  )}
                  {tier === 3 && hasEntries && (
                    <RiSparkling2Line size={10} style={{ color: BRAND.magenta }} />
                  )}
                </div>
              </div>

              {/* Body */}
              {loading && cell.inMonth && !hasEntries ? (
                <div className="space-y-1.5">
                  <div className="h-3.5 bg-white/60 rounded animate-pulse w-2/3" />
                  <div className="h-2 bg-white/60 rounded animate-pulse w-1/2" />
                </div>
              ) : hasEntries ? (
                <div className="space-y-2.5">
                  <div>
                    <p className="font-extrabold tabular-nums leading-none tracking-tight text-[16px]"
                       style={{ color: look.text }}>
                      {fmtCompact(totalMs)}
                    </p>
                    <p className="text-[10.5px] text-[#64748B] mt-1 leading-none font-medium">
                      {showUserSummary
                        ? `${userIds.size} ${userIds.size === 1 ? 'person' : 'people'} · ${items.length}`
                        : `${items.length} ${items.length === 1 ? 'session' : 'sessions'}`}
                    </p>
                  </div>

                  {/* 24h timeline */}
                  <div className="relative h-1.5 bg-white/70 rounded-full overflow-hidden border border-white/60">
                    {bars.map((b, i) => (
                      <span
                        key={i}
                        className={`absolute top-0 bottom-0 rounded-full ${b.open ? 'animate-pulse' : ''}`}
                        style={{
                          left: `${b.left}%`,
                          width: `${b.w}%`,
                          background: b.open
                            ? `linear-gradient(90deg,${BRAND.cyan},${BRAND.magenta})`
                            : BRAND.gradient,
                          opacity: b.open ? 1 : 0.85,
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : cell.inMonth && !isFuture ? (
                <p className="text-[10.5px] text-[#CBD5E1] italic mt-1">·</p>
              ) : null}

              {/* Hover arrow */}
              {clickable && (
                <span
                  className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-extrabold"
                  style={{ color: BRAND.purple }}
                >
                  →
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Footer / legend ─────────────────────────────────────────────────── */}
      <div className="relative px-7 py-4 border-t border-[#EEF2F6] flex items-center justify-between gap-4 flex-wrap text-[11px]">
        <div className="flex items-center gap-3">
          <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-[#94A3B8]">Activity</span>
          <div className="flex items-center gap-1">
            {TIER_LOOK.map((s, i) => (
              <span
                key={i}
                className="inline-block h-2.5 rounded-full"
                style={{
                  width: 22,
                  background: i === 0 ? '#F1F5F9' : s.bg,
                  border: '1px solid rgba(255,255,255,0.6)',
                  boxShadow: i > 0 ? '0 1px 2px rgba(9,132,227,0.10)' : 'none',
                }}
                title={['< 30m', '30m – 3h', '3 – 6h', '6h+'][i]}
              />
            ))}
          </div>
          <span className="text-[10px] text-[#94A3B8]">Less → More</span>
        </div>
        <div className="flex items-center gap-1.5 text-[#64748B]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ background: BRAND.magenta }} />
            <span className="relative inline-flex rounded-full h-2 w-2"
                  style={{ background: BRAND.magenta }} />
          </span>
          Active session
        </div>
      </div>
    </div>
  );
}

function RoundBtn({ children, onClick, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="w-10 h-10 rounded-full bg-white border border-[#EEF2F6] text-[#475569] hover:text-[#0984e3] hover:border-[#0984e3]/40 hover:shadow-[0_6px_16px_-6px_rgba(9,132,227,0.35)] active:scale-95 transition-all flex items-center justify-center cursor-pointer"
    >
      {children}
    </button>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone, tint, pulse }) {
  return (
    <div className="relative rounded-2xl p-3.5 bg-white border border-[#EEF2F6] hover:shadow-[0_8px_22px_-10px_rgba(9,132,227,0.30)] transition-shadow overflow-hidden">
      {/* Soft top sheen */}
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: tone, opacity: 0.85 }} />
      <div className="flex items-center gap-3">
        <div
          className="relative w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: tint, border: `1px solid ${tone}25` }}
        >
          {pulse && (
            <span
              className="absolute inset-0 rounded-xl animate-ping"
              style={{ background: `${tone}25` }}
            />
          )}
          <Icon size={16} style={{ color: tone }} className="relative" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] leading-none">
            {label}
          </p>
          <p className="text-[#0F172A] font-extrabold text-[17px] tabular-nums leading-tight mt-1 tracking-tight">
            {value}
          </p>
          {sub && (
            <p className="text-[10px] text-[#94A3B8] mt-0.5 truncate font-medium" title={sub}>
              {sub}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
